<template>
  <div>
    <label :for="id" class="block text-sm font-medium text-gray-300 mb-1">
      {{ label }}
    </label>
    <div class="relative" ref="cityDropdownContainer">
      <div
        class="w-full bg-gray-700 border border-gray-600 rounded-md overflow-hidden"
        :class="{ 'border-red-500': error }"
      >
        <div class="relative">
          <input
            :type="type"
            :id="id"
            v-model="localSearch"
            :placeholder="placeholder"
            class="w-full bg-gray-700 py-2 px-3 text-white placeholder-gray-400 focus:outline-none"
            :class="inputClass"
            @focus="showDropdown = true"
            @input="onInput"
            autocomplete="off"
            @blur="onBlur"
          />
          <button
            type="button"
            class="absolute right-0 top-0 h-full px-2 text-gray-400"
            @click="toggleDropdown"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </button>
        </div>
        <div
          v-if="showDropdown"
          class="max-h-40 overflow-y-auto border-t border-gray-600"
        >
          <div
            v-for="city in filteredCities"
            :key="city"
            @click="selectCity(city)"
            class="py-1 px-3 hover:bg-gray-600 cursor-pointer text-white"
          >
            {{ city }}
          </div>
          <div
            v-if="filteredCities.length === 0"
            class="py-1 px-3 text-gray-400"
          >
            Nema rezultata
          </div>
        </div>
      </div>
    </div>
    <p v-if="error" class="mt-1 text-xs text-red-500">
      {{ error }}
    </p>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import cityService from '@/services/cityService';

export default defineComponent({
  name: 'CityDropdown',
  props: {
    id: {
      type: String,
      default: 'city',
    },
    label: {
      type: String,
      default: 'Mesto',
    },
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Pretraži gradove...',
    },
    error: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    inputClass: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'blur'],
  setup(props, { emit }) {
    const cities = ref<string[]>([]);
    const filteredCities = ref<string[]>([]);
    const showDropdown = ref(false);
    const localSearch = ref(props.modelValue);
    const cityDropdownContainer = ref<HTMLElement | null>(null);

    const fetchCities = async () => {
      try {
        cities.value = await cityService.getCities();
        filteredCities.value = [...cities.value];
      } catch (error) {
        console.error('Failed to fetch cities:', error);
        cities.value = [];
        filteredCities.value = [];
      }
    };

    const filterCities = () => {
      if (!localSearch.value) {
        filteredCities.value = [...cities.value];
        return;
      }

      const searchTerm = localSearch.value.toLowerCase();
      filteredCities.value = cities.value.filter((city) =>
        city.toLowerCase().includes(searchTerm)
      );
    };

    const selectCity = (city: string) => {
      localSearch.value = city;
      emit('update:modelValue', city);
      showDropdown.value = false;
    };

    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value;
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        cityDropdownContainer.value &&
        !cityDropdownContainer.value.contains(event.target as Node) &&
        showDropdown.value
      ) {
        showDropdown.value = false;
      }
    };

    const onInput = () => {
      emit('update:modelValue', localSearch.value);
      filterCities();
    };

    const onBlur = () => {
      // We use setTimeout to allow click events on dropdown items to complete
      // before potentially closing the dropdown
      setTimeout(() => {
        emit('blur');
      }, 200);
    };

    // Watch for changes in the model value from parent
    watch(
      () => props.modelValue,
      (newValue) => {
        localSearch.value = newValue;
      }
    );

    // Watch for changes in the dropdown state
    watch(showDropdown, (isOpen) => {
      if (!isOpen) {
        setTimeout(() => {
          if (!showDropdown.value) {
            filterCities();
          }
        }, 200);
      }
    });

    // Watch for changes in the local search
    watch(localSearch, () => {
      filterCities();
    });

    onMounted(() => {
      fetchCities();
      document.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      cities,
      filteredCities,
      showDropdown,
      localSearch,
      cityDropdownContainer,
      filterCities,
      selectCity,
      toggleDropdown,
      onInput,
      onBlur,
    };
  },
});
</script>

<style scoped>
/* Custom scrollbar styling for city dropdown */
div::-webkit-scrollbar {
  width: 8px;
}

div::-webkit-scrollbar-track {
  background: #2d3748;
}

div::-webkit-scrollbar-thumb {
  background-color: #4a5568;
  border-radius: 4px;
}

div::-webkit-scrollbar-thumb:hover {
  background-color: #718096;
}
</style>
